// =========================================================================
// HOME (app-wide) THEMES - 6 free + 6 premium, all dark and flat.
// Old saved theme ids (from the former 29-theme list) are mapped to the nearest new theme by
// LEGACY_HOME_THEME_MAP, so nobody's saved theme breaks. Premium ones unlock with an active Premium plan.
// public/home-themes/*.svg are only the picker swatches.
// The old 'light' / 'dark' values of profile.appBackground keep working.
// =========================================================================

export type HomeTheme = {
  id: string
  label: string
  url: string // tileable SVG pattern (also used as the picker swatch) - OR a full-bleed
               // photo/illustration when `cover` is true (see below).
  // Optional small landscape preview (picker tiles only). Cover scenes are tall, so a centre-crop of
  // the full image would show an empty sky; the thumb re-composes the same scene for a wide tile.
  thumb?: string
  premium: boolean
  dark: boolean
  bg: string
  paper: string
  soft: string
  line: string
  ink: string
  muted: string
  a: string
  b: string
  // When true, `url` is a single full-screen image (not a small repeating pattern):
  // rendered with background-size: cover / no-repeat / centered instead of tiled at
  // 160x160. Used for photographic/illustrated scenes (e.g. the sakura sunset theme)
  // where tiling would look broken.
  cover?: boolean
  // "Clean" themes = the clean-styled wallpapers from the chat section (soft gradients / quiet patterns,
  // no moon, sun or other objects), reused 1:1 on the home page. Shown in their own "Clean" group in the picker.
  clean?: boolean
  // Flat theme: plain background colour, no pattern / picture / glow (all current themes).
  flat?: boolean
  // Optional exact accent (used as-is, not darkened) and a two-stop accent gradient
  // (wordmark, + button, active tab). Themes without these keep the old single-hue look.
  accent?: string
  gradient?: [string, string]
}

/** Theme used when the person never picked one (appBackground empty / 'none'): the dark Pink Animora theme. */
export const DEFAULT_HOME_THEME_ID = 'ht-black'

// All themes are DARK on purpose: light text on a dark surface everywhere, so text can never end up white-on-white or
// black-on-black. `a` = light accent used for links / active icons (>= 7:1 on the card colour), `accent` / `b` = button fill
// (white text on it is >= 4.5:1). Colours are DEEP and SATURATED (high contrast): the page is near-black, cards are a clearly lighter rich shade, borders are vivid. Free + Premium share the same punch; Premium has the deepest tones. Themes are flat (no pattern).
export const HOME_THEMES: HomeTheme[] = [
  // The one and only theme: pure deep black, flat, no lines. Buttons are pencil-grey with white text.
  { id: 'ht-black', label: 'Deep black', url: '', premium: false, dark: true, flat: true, bg: '#000000', paper: '#000000', soft: '#111113', line: '#1a1a1d', ink: '#ffffff', muted: '#9b9ba3', a: '#e9e9ec', b: '#3a3a40', accent: '#3a3a40', gradient: ['#3a3a40', '#3a3a40'] },
]

// Former theme ids -> nearest current theme. Any other old id (and the old 'light' / 'dark' values) falls back to the default.
export const LEGACY_HOME_THEME_MAP: Record<string, string> = {}

/** Maps any saved id (current or legacy) to a current theme id. Unknown ids, 'none', 'light' and 'dark' give the default. */
export const resolveHomeThemeId = (id?: string): string => {
  if (!id || id === 'none' || id === 'light' || id === 'dark') return DEFAULT_HOME_THEME_ID
  if (HOME_THEMES.some((t) => t.id === id)) return id
  return LEGACY_HOME_THEME_MAP[id] ?? DEFAULT_HOME_THEME_ID
}

export const getHomeTheme = (id?: string): HomeTheme | undefined => {
  const rid = resolveHomeThemeId(id)
  return HOME_THEMES.find((t) => t.id === rid)
}

// Readable text colour for a given background (picks black or white, whichever has the higher contrast).
const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '')
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)]
}
const luminance = ([r, g, b]: [number, number, number]) => {
  const ch = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4) }
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b)
}
/** Text colour that stays readable on top of the theme's accent fill (accent = theme.b darkened 20%). */
export const onAccentColor = (t: HomeTheme): string => {
  const [r, g, b] = (t.accent ? hexToRgb(t.accent) : hexToRgb(t.b).map((v) => Math.round(v * 0.8))) as [number, number, number]
  const L = luminance([r, g, b])
  return (1.05) / (L + 0.05) >= (L + 0.05) / 0.05 ? '#ffffff' : '#0b0b10'
}

// CSS variables that recolor the whole app shell (cards, bars, text, lines).
export const homeThemeVars = (t: HomeTheme): Record<string, string> => ({
  '--bg': t.bg,
  '--paper': t.paper,
  '--soft': t.soft,
  '--soft-2': `color-mix(in srgb, ${t.soft} 80%, ${t.line})`,
  '--ink': t.ink,
  '--ink-2': `color-mix(in srgb, ${t.ink} 82%, ${t.paper})`,
  '--muted': t.muted,
  '--ink-3': `color-mix(in srgb, ${t.ink} 72%, ${t.paper})`,
  '--line': t.line,
  '--accent': t.accent ?? `color-mix(in srgb, ${t.b} 80%, #000)`,
  '--solid': t.accent ?? `color-mix(in srgb, ${t.b} 80%, #000)`,
  '--accent-gradient': t.gradient ? `linear-gradient(135deg, ${t.gradient[0]} 0%, ${t.gradient[1]} 100%)` : (() => { const c = t.accent ?? `color-mix(in srgb, ${t.b} 80%, #000)`; return `linear-gradient(135deg, ${c} 0%, ${c} 100%)` })(),
  '--accent-glow': `color-mix(in srgb, ${t.accent ?? t.b} 22%, transparent)`,
  '--accent-ink': t.dark ? t.a : `color-mix(in srgb, ${t.b} 80%, #000)`,
  '--cyan': t.dark ? t.a : `color-mix(in srgb, ${t.b} 80%, #000)`,
  '--magenta': t.dark ? t.a : `color-mix(in srgb, ${t.b} 80%, #000)`,
  '--accent-soft': `color-mix(in srgb, ${t.a} 14%, transparent)`,
  '--accent-tint': `color-mix(in srgb, ${t.a} 14%, transparent)`,
  '--accent-line': `color-mix(in srgb, ${t.a} 38%, transparent)`,
  // Themes with a two-stop gradient also recolor the home-screen gradient tokens (wordmark, + buttons, rings, glows).
  // EVERY theme now drives the home gradient (wordmark, + buttons, Add friend, rings, active tab). Before, only gradient themes
  // did, so all other themes fell back to a fixed blue-violet that clashed with their own colours.
  '--hm-a': t.gradient ? t.gradient[0] : `color-mix(in srgb, ${t.accent ?? `color-mix(in srgb, ${t.b} 80%, #000)`} 82%, ${t.a})`,
  '--hm-b': t.gradient ? t.gradient[1] : (t.accent ?? `color-mix(in srgb, ${t.b} 80%, #000)`),
  '--line-strong': `color-mix(in srgb, ${t.line} 65%, ${t.ink})`,
  // Semantic text tokens: every screen reads these, so text always matches the active theme.
  '--on-accent': onAccentColor(t),
  '--text-on-accent': onAccentColor(t),
  '--text-primary': t.ink,
  '--text-secondary': `color-mix(in srgb, ${t.ink} 72%, ${t.paper})`,
  '--text-muted': t.muted,
  '--nav-inactive': t.dark ? `color-mix(in srgb, ${t.ink} 68%, ${t.bg})` : `color-mix(in srgb, ${t.ink} 62%, ${t.bg})`,
  // GLASS LOOK: a deep, rich "mesh" of soft colour blobs (accent -> second accent -> deep tone) sits behind
  // everything, and every bar / card / sheet is frosted glass on top of it (see the GLASS section at the end of
  // globals.css). Cover themes (one full-bleed picture) still use the picture instead.
  '--home-pattern': t.flat
    ? 'none'
    : t.cover
    ? `url(${t.url})`
    : t.gradient
    // Gradient themes (Black): near-flat background, just a faint wash of the two accent colours.
    ? [
        `radial-gradient(85% 35% at 95% 6%, color-mix(in srgb, ${t.gradient[0]} 7%, transparent) 0%, transparent 75%)`,
        `radial-gradient(80% 35% at 0% 100%, color-mix(in srgb, ${t.gradient[1]} 6%, transparent) 0%, transparent 75%)`,
        t.bg,
      ].join(', ')
    : [
        `radial-gradient(70% 38% at 10% -2%, color-mix(in srgb, ${t.a} ${t.dark ? 20 : 40}%, transparent) 0%, transparent 72%)`,
        `radial-gradient(60% 42% at 100% 22%, color-mix(in srgb, ${t.b} ${t.dark ? 16 : 32}%, transparent) 0%, transparent 72%)`,
        `radial-gradient(75% 45% at -5% 62%, color-mix(in srgb, ${t.b} ${t.dark ? 12 : 26}%, ${t.bg}) 0%, transparent 74%)`,
        `radial-gradient(70% 42% at 100% 100%, color-mix(in srgb, ${t.a} ${t.dark ? 14 : 30}%, transparent) 0%, transparent 74%)`,
        `linear-gradient(165deg, color-mix(in srgb, ${t.a} ${t.dark ? 12 : 18}%, ${t.bg}) 0%, ${t.bg} 55%, color-mix(in srgb, ${t.b} ${t.dark ? 16 : 22}%, ${t.bg}) 100%)`,
      ].join(', '),
  // Glass tokens (read by the GLASS section in globals.css). Dark themes: smoky tinted glass with a bright rim.
  // Light themes: milky white glass.
  '--glass': t.dark
    ? `color-mix(in srgb, ${t.paper} 92%, transparent)`   // cards keep their full deep colour (was 70% = washed out)
    : `rgba(255,255,255,.88)`,
  '--glass-bar': t.dark
    ? `color-mix(in srgb, ${t.paper} 86%, ${t.bg})`
    : `rgba(255,255,255,.90)`,
  // Sheets / dialogs are fully OPAQUE: a see-through sheet let the page behind it show through the text
  // (and nested backdrop-filters are unreliable in Android WebView), so lists inside looked broken.
  '--glass-sheet': t.dark
    ? t.paper
    : `#ffffff`,
  '--glass-border': t.dark ? `color-mix(in srgb, ${t.line} 75%, transparent)` : 'rgba(15,23,42,.13)',
  '--glass-rim': 'transparent',
  '--glass-shadow': 'none',
  '--home-pattern-size': t.cover ? 'cover' : 'auto',
  '--home-pattern-repeat': 'no-repeat',
  '--home-pattern-position': t.cover ? 'center' : '0 0',
})
